// SecJournal.jsx — Journal d'audit des actions admin / gouvernante / Direction
// Entrées stockées dans journal7 (Supabase, append-only)
// Structure d'une entrée :
// { id, ts, qui, role, action, objet, cible, bid, fid, detail }
import React, { useState, useMemo } from "react";

// ─── Labels et icônes par type d'action ──────────────────────────────────────
const ACTION_META = {
  override_statut:    { e: '🏠', l: 'Override statut',       cat: 'Statut' },
  verdict_controle:   { e: '🔍', l: 'Verdict contrôle',      cat: 'Contrôle' },
  facture_payee:      { e: '💶', l: 'Facture payée',          cat: 'Facturation' },
  facture_annulee:    { e: '🚫', l: 'Facture annulée',        cat: 'Facturation' },
  facture_emise:      { e: '📤', l: 'Facture émise',          cat: 'Facturation' },
  relance_envoyee:    { e: '🔔', l: 'Relance envoyée',        cat: 'Facturation' },
  relance_supprimee:  { e: '🗑',  l: 'Relance supprimée',     cat: 'Facturation' },
  email_facture:      { e: '📧', l: 'Email facture envoyé',   cat: 'Facturation' },
  controle_abandonne: { e: '⏭',  l: 'Contrôle abandonné',    cat: 'Contrôle' },
  fin_journee_anomalie: { e: '⚠️', l: 'Fin de journée — anomalie', cat: 'Pointages' },
  fin_journee_ok:       { e: '✅', l: 'Fin de journée — RAS',      cat: 'Pointages' },
};

const ROLES_META = {
  admin:      { e: '🔑', l: 'Admin',       c: '#1d4ed8' },
  gerant:     { e: '👔', l: 'Direction',   c: '#7c3aed' },
  gouvernante:{ e: '🏠', l: 'Gouvernante', c: '#0369a1' },
  salarie:    { e: '👤', l: 'Salarié',     c: '#6b5d52' },
};

function fmtTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function SecJournal({ journal, biens, clients }) {
  const [filtreAction, setFiltreAction] = useState('all');
  const [filtreRole,   setFiltreRole]   = useState('all');
  const [filtreBien,   setFiltreBien]   = useState('all');
  const [filtreTexte,  setFiltreTexte]  = useState('');
  const [filtreDateDeb,setFiltreDateDeb]= useState('');
  const [filtreDateFin,setFiltreDateFin]= useState('');
  const [page, setPage] = useState(0);
  const PER_PAGE = 50;

  const entries = useMemo(() => {
    const q = filtreTexte.toLowerCase().trim();
    return (journal || [])
      .filter(e => filtreAction === 'all' || e.action === filtreAction)
      .filter(e => filtreRole   === 'all' || e.role   === filtreRole)
      .filter(e => filtreBien   === 'all' || e.bid    === filtreBien)
      .filter(e => !filtreDateDeb || (e.ts || '') >= filtreDateDeb)
      .filter(e => !filtreDateFin || (e.ts || '') <= filtreDateFin + 'T23:59:59')
      .filter(e => !q || [e.qui, e.action, e.cible, e.detail, e.objet]
        .filter(Boolean).join(' ').toLowerCase().includes(q))
      .sort((a, b) => (b.ts || '').localeCompare(a.ts || ''));
  }, [journal, filtreAction, filtreRole, filtreBien, filtreTexte, filtreDateDeb, filtreDateFin]);

  const paginated = entries.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const nbPages = Math.ceil(entries.length / PER_PAGE);

  // Compteurs par catégorie pour les filtres rapides
  const nbParAction = useMemo(() => {
    const m = {};
    (journal || []).forEach(e => { m[e.action] = (m[e.action] || 0) + 1; });
    return m;
  }, [journal]);

  const biensActifs = useMemo(() => {
    const bids = new Set((journal || []).map(e => e.bid).filter(Boolean));
    return (biens || []).filter(b => bids.has(b.id));
  }, [journal, biens]);

  const resetFiltres = () => {
    setFiltreAction('all'); setFiltreRole('all'); setFiltreBien('all');
    setFiltreTexte(''); setFiltreDateDeb(''); setFiltreDateFin('');
    setPage(0);
  };

  const hasFiltres = filtreAction !== 'all' || filtreRole !== 'all' || filtreBien !== 'all'
    || filtreTexte || filtreDateDeb || filtreDateFin;

  return (
    <div style={{ padding: '0 4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 className="D" style={{ fontSize: 21, marginBottom: 3 }}>📋 Journal des actions</h2>
          <div style={{ fontSize: 12, color: 'var(--si)' }}>
            Toutes les actions admin, gouvernante et Direction — {(journal || []).length} entrée{(journal || []).length !== 1 ? 's' : ''}
          </div>
        </div>
        {hasFiltres && (
          <button onClick={resetFiltres} className="btn bgh sm" style={{ fontSize: 12 }}>✕ Effacer les filtres</button>
        )}
      </div>

      {/* ── Filtres ── */}
      <div style={{ background: 'var(--bg)', border: '1px solid var(--li)', borderRadius: 12, padding: '14px 16px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Recherche libre */}
        <input
          type="text"
          placeholder="🔍 Rechercher (nom, appartement, détail…)"
          value={filtreTexte}
          onChange={e => { setFiltreTexte(e.target.value); setPage(0); }}
          style={{ width: '100%', fontSize: 13, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--li)', background: 'var(--card)', boxSizing: 'border-box' }}
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          {/* Filtre type d'action */}
          <div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--si)', marginBottom: 4, fontWeight: 700 }}>Action</div>
            <select value={filtreAction} onChange={e => { setFiltreAction(e.target.value); setPage(0); }}
              style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--li)', background: 'var(--card)' }}>
              <option value="all">Toutes les actions</option>
              {Object.entries(ACTION_META).map(([k, v]) => (
                nbParAction[k] ? <option key={k} value={k}>{v.e} {v.l} ({nbParAction[k]})</option> : null
              ))}
            </select>
          </div>

          {/* Filtre rôle */}
          <div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--si)', marginBottom: 4, fontWeight: 700 }}>Profil</div>
            <select value={filtreRole} onChange={e => { setFiltreRole(e.target.value); setPage(0); }}
              style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--li)', background: 'var(--card)' }}>
              <option value="all">Tous les profils</option>
              {Object.entries(ROLES_META).map(([k, v]) => (
                <option key={k} value={k}>{v.e} {v.l}</option>
              ))}
            </select>
          </div>

          {/* Filtre appartement */}
          <div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--si)', marginBottom: 4, fontWeight: 700 }}>Appartement</div>
            <select value={filtreBien} onChange={e => { setFiltreBien(e.target.value); setPage(0); }}
              style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--li)', background: 'var(--card)' }}>
              <option value="all">Tous les appartements</option>
              {biensActifs.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>

          {/* Filtre date début */}
          <div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--si)', marginBottom: 4, fontWeight: 700 }}>Du</div>
            <input type="date" value={filtreDateDeb} onChange={e => { setFiltreDateDeb(e.target.value); setPage(0); }}
              style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--li)', background: 'var(--card)', boxSizing: 'border-box' }}/>
          </div>

          {/* Filtre date fin */}
          <div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--si)', marginBottom: 4, fontWeight: 700 }}>Au</div>
            <input type="date" value={filtreDateFin} onChange={e => { setFiltreDateFin(e.target.value); setPage(0); }}
              style={{ width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--li)', background: 'var(--card)', boxSizing: 'border-box' }}/>
          </div>
        </div>
      </div>

      {/* ── Résultats ── */}
      {entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--si)' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
          <div style={{ fontSize: 14 }}>{hasFiltres ? 'Aucune entrée ne correspond aux filtres.' : 'Le journal est vide pour l\'instant.'}</div>
          {!hasFiltres && <div style={{ fontSize: 12, marginTop: 4 }}>Les actions seront enregistrées ici dès qu\'elles sont effectuées.</div>}
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--si)', marginBottom: 8 }}>
            {entries.length} résultat{entries.length !== 1 ? 's' : ''}
            {hasFiltres ? ' (filtres actifs)' : ''}
            {nbPages > 1 && ` — page ${page + 1}/${nbPages}`}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {paginated.map((e, i) => {
              const meta = ACTION_META[e.action] || { e: '•', l: e.action, cat: '' };
              const role = ROLES_META[e.role] || { e: '👤', l: e.role || '?', c: 'var(--si)' };
              const bien = e.bid ? (biens || []).find(b => b.id === e.bid) : null;
              return (
                <div key={e.id || i} className="card" style={{ padding: '11px 14px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  {/* Icône action */}
                  <div style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>{meta.e}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 3 }}>
                      {/* Type action */}
                      <span style={{ fontWeight: 700, fontSize: 13 }}>{meta.l}</span>
                      {/* Rôle badge */}
                      <span style={{ fontSize: 10, fontWeight: 700, background: role.c + '18', color: role.c, padding: '2px 7px', borderRadius: 999 }}>
                        {role.e} {e.qui || role.l}
                      </span>
                      {/* Appartement */}
                      {bien && (
                        <span style={{ fontSize: 11, background: 'var(--bg)', color: 'var(--si)', padding: '2px 7px', borderRadius: 999, border: '1px solid var(--li)' }}>
                          🏠 {bien.name}
                        </span>
                      )}
                      {/* Objet (ex: numéro facture) */}
                      {e.objet && !bien && (
                        <span style={{ fontSize: 11, background: 'var(--bg)', color: 'var(--si)', padding: '2px 7px', borderRadius: 999, border: '1px solid var(--li)' }}>
                          {e.objet}
                        </span>
                      )}
                      {e.objet && bien && (
                        <span style={{ fontSize: 11, color: 'var(--si)' }}>{e.objet}</span>
                      )}
                    </div>
                    {/* Détail */}
                    {e.detail && (
                      <div style={{ fontSize: 12, color: 'var(--si)', marginBottom: 2 }}>{e.detail}</div>
                    )}
                    {/* Cible / destinataire */}
                    {e.cible && (
                      <div style={{ fontSize: 12, color: '#6b5d52', fontStyle: 'italic' }}>{e.cible}</div>
                    )}
                  </div>
                  {/* Timestamp */}
                  <div style={{ fontSize: 11, color: 'var(--si)', flexShrink: 0, textAlign: 'right', minWidth: 100 }}>
                    {fmtTs(e.ts)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {nbPages > 1 && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="btn bgh sm">← Précédent</button>
              <span style={{ fontSize: 12, color: 'var(--si)', alignSelf: 'center' }}>Page {page + 1} / {nbPages}</span>
              <button onClick={() => setPage(p => Math.min(nbPages - 1, p + 1))} disabled={page === nbPages - 1} className="btn bgh sm">Suivant →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
